import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { storeAuthToken } from '@/lib/session'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ArrowRight, Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useCurrentUserQuery } from '../api/useCurrentUser'
import { useLogin } from '../api/useLogin'
import { useAuth } from './auth-provider'

const schema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  rememberMe: z.boolean().optional(),
})

type FormData = z.infer<typeof schema>
const POST_FORCE_RESET_REDIRECT_KEY = 'post_force_reset_redirect'

export function LoginForm() {
  const { login } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [cardActivated] = useState(false)
  // const [formReady, setFormReady] = useState(false)
  const navigate = useNavigate()
  const { redirect } = useSearch({ from: '/login' })
  const loginMutation = useLogin()
  const currentUserQuery = useCurrentUserQuery({ enabled: false })

  const form = useForm<FormData>({
    defaultValues: { email: '', password: '', rememberMe: false },
    mode: 'onChange',
  })

  // useEffect(() => {
  //   setCardActivated(true)
    // const frame = requestAnimationFrame(() => setFormReady(true))
    // return () => cancelAnimationFrame(frame)
  // }, [])

  async function onSubmit(values: FormData) {
    try {
      setIsLoading(true)
      const validatedData = schema.parse(values)

      const response = await loginMutation.mutateAsync({
        usernameOrEmail: validatedData.email,
        password: validatedData.password,
        rememberMe: validatedData.rememberMe,
      })

      const maybeToken = response.data as any
      const accessToken: string | undefined = maybeToken?.accessToken
      const tokenType: string | undefined = maybeToken?.tokenType || 'Bearer'
      const forcePasswordReset = Boolean(maybeToken?.passwordResetRequired)

      if (!accessToken) {
        const message = (maybeToken?.data?.message || maybeToken?.message || 'Invalid credentials') as string
        throw new Error(message)
      }

      storeAuthToken(accessToken, tokenType, validatedData.rememberMe)
      login({ email: validatedData.email, roles: ['ROLE_USER'] })
      toast.success('Signed in successfully')

      const safeTo = (() => {
        const fallback = '/dashboard'
        if (!redirect) return fallback
        try {
          const u = new URL(redirect, window.location.origin)
          const pathname = u.pathname
          const search = u.search || ''
          if (pathname === '/_authenticated/dashboard') return '/dashboard' as const
          const cleanPathname = pathname.startsWith('/_authenticated')
            ? pathname.replace('/_authenticated', '')
            : pathname
          const allowed = new Set([
            '/',
            '/login',
            '/signup',
            '/forgot-password',
            '/reset-password',
            '/dashboard',
            '/membership/profile',
          ])
          return allowed.has(cleanPathname) ? ((cleanPathname + search) as any) : fallback
        }
        catch {
          if (redirect.startsWith('/')) {
            const cleaned = redirect.startsWith('/_authenticated')
              ? redirect.replace('/_authenticated', '')
              : redirect
            if (cleaned === '/dashboard') return '/dashboard' as const
            const [pathname] = cleaned.split('?')
            if (pathname === '/membership/profile')
              return cleaned as any
            return pathname === '/dashboard' ? (cleaned as any) : fallback
          }
          return fallback
        }
      })()

      if (forcePasswordReset) {
        try {
          window.localStorage.setItem('force_password_reset', '1')
          window.sessionStorage.setItem(POST_FORCE_RESET_REDIRECT_KEY, safeTo)
        }
        catch {}
      }

      const meResult = await currentUserQuery.refetch()
      if (meResult.data) {
        login(meResult.data)
      }

      if (forcePasswordReset) {
        navigate({ to: '/force-password-reset', replace: true })
        return
      }

      try {
        window.sessionStorage.removeItem(POST_FORCE_RESET_REDIRECT_KEY)
      }
      catch {}

      navigate({ to: safeTo as any, reloadDocument: true })
    }
    catch (error) {
      if (error instanceof z.ZodError) {
        error.issues.forEach((issue) => {
          if (issue.path[0]) {
            form.setError(issue.path[0] as keyof FormData, { message: issue.message })
          }
        })
      }
      else if (error instanceof Error) {
        toast.error(error.message)
      }
    }
    finally {
      setIsLoading(false)
    }
  }

  return (
    <Card
      className={`relative mx-auto w-full max-w-lg overflow-hidden rounded-[32px] border border-white/70 bg-white/80 text-slate-900 shadow-[0_35px_120px_rgba(15,23,42,0.25)] backdrop-blur-[40px] transition-[transform,opacity] duration-700 ease-out dark:border-slate-700/80 dark:bg-slate-900/85 dark:text-slate-100 ${cardActivated ? 'opacity-100 translate-y-0 scale-100' : 'opacity-90 translate-y-6 scale-[0.98]'}`}
    >
      <div className="h-[3px] w-full bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-400" />
      <CardHeader className="space-y-3 border-b border-slate-100/70 px-6 pb-6 pt-6 dark:border-slate-700/70">
        <div className="flex flex-col gap-2">
          <span className="inline-flex w-fit items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold tracking-[0.25em] text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Secure sign-in
          </span>
          <CardTitle className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Welcome back</CardTitle>
          <CardDescription className="text-slate-600 dark:text-slate-300">
            Access your UIBFS Membership dashboard to request leave, update entitlements, and stay aligned with approvals.
          </CardDescription>
        </div>
      </CardHeader>

      <CardContent className="space-y-6 px-6 pb-8 pt-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              name="email"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel required htmlFor="email" className="text-sm font-medium text-slate-700 dark:text-slate-200">Email Address</FormLabel>
                  <FormControl>
                    <div className="relative rounded-[14px] border border-slate-200 bg-white p-1 text-sm transition focus-within:border-emerald-500 dark:border-slate-600 dark:bg-slate-800/90">
                      <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500 dark:text-slate-300" />
                      <Input
                        type="email"
                        id="email"
                        placeholder="Enter your email"
                        autoComplete="email"
                        className="h-11 w-full rounded-[8px] border-0 bg-transparent pl-10 pr-3 text-slate-900 placeholder:text-slate-500 dark:text-slate-100 dark:placeholder:text-slate-400"
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="password"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel required htmlFor="password" className="text-sm font-medium text-slate-700 dark:text-slate-200">Password</FormLabel>
                  <FormControl>
                    <div className="relative rounded-[14px] border border-slate-200 bg-white p-1 text-sm transition focus-within:border-emerald-500 dark:border-slate-600 dark:bg-slate-800/90">
                      <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500 dark:text-slate-300" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        autoComplete="current-password"
                        className="h-11 w-full rounded-[8px] border-0 bg-transparent pl-10 pr-12 text-slate-900 placeholder:text-slate-500 dark:text-slate-100 dark:placeholder:text-slate-400"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-pressed={showPassword}
                        className="absolute right-0 top-0 h-full px-3 py-2 text-slate-500 hover:bg-transparent dark:text-slate-300 dark:hover:text-slate-100"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex items-center justify-between text-sm text-slate-600 dark:text-slate-300">
              <FormField
                name="rememberMe"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-3">
                    <Checkbox
                      id="rememberMe"
                      name="rememberMe"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="border-2 border-primary focus:ring-2 dark:border-slate-400"
                    />
                    <Label htmlFor="rememberMe" className="text-sm font-normal text-slate-700 dark:text-slate-200">Remember me</Label>
                  </FormItem>
                )}
              />
              <Button variant="link" className="px-0 text-sm font-semibold text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-50" asChild>
                <a href="/forgot-password">Forgot password?</a>
              </Button>
            </div>

            <Button
              type="submit"
              className="w-full rounded-[18px] bg-gradient-to-r from-primary to-[color-mix(in_oklab,var(--primary),white_22%)] text-primary-foreground font-semibold shadow-lg transition hover:from-[color-mix(in_oklab,var(--primary),white_10%)] hover:to-[color-mix(in_oklab,var(--primary),white_28%)]"
              disabled={isLoading || loginMutation.isPending}
            >
              {isLoading || loginMutation.isPending ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  <span>Signing in...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <span>Sign in</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
